import * as R from "ramda";
import moment from "moment";
import {networkError} from "./ui";
import {getNotifications$, markNotificationsSeen$, switchToActiveNotifications$} from "../services/notifications";
import {db, dehydrate, hydrate} from "../reducers/persistence";
import {currentAuthenticatedUserId, setArchivePending} from "../reducers/ui";
import {GET_MY_PROFILE_DONE} from "./users";
import {extractSubview, extractView} from "./view";

export const ADD_NOTIFICATIONS = 'ADD_NOTIFICATIONS';
export const addNotifications = (notifications) => ({ type: ADD_NOTIFICATIONS, payload: { notifications }});

export const REMOVE_NOTIFICATIONS = 'REMOVE_NOTIFICATIONS';
export const removeNotifications = (notificationIds) => ({ type: REMOVE_NOTIFICATIONS, payload: { notificationIds }});

const translateDateCreated = R.over(R.lensProp('datecreated'), datecreated => moment(datecreated + 'Z'));
const insertCurrentUser = (currentUser) => R.set(R.lensProp('userid'), currentUser);

const MAX_SIZE = 3000;

export const persistNotifications = async (notificationsToLog, currentUser) => {
  notificationsToLog = R.map(R.pipe(translateDateCreated, insertCurrentUser(currentUser), dehydrate), notificationsToLog);
  for (let notification of notificationsToLog) {
    try {
      await db.notifications.put(notification);
    } catch (error) {
      console.log(error);
    }
  }
};

export const depersistNotifications = async notificationIds => {
  for (let id of notificationIds) {
    try {
      await db.notifications.delete(id);
    } catch (error) {
      console.log(error);
    }
  }
};

const handleNotifications = async (dispatch, getState, notes) => {
  let notificationsSeen = [];
  let notificationsToLog = [];
  let currentUser = currentAuthenticatedUserId(getState());
  try {
    for (let note of notes) {
      try {
        switch (note.segmenttype) {
          case 'archive_request':
            dispatch(setArchivePending(note.associatedid, { state: 'requested' }));
            notificationsSeen.push(note.historyid);
            break;
          case 'archive_accept':
            dispatch(setArchivePending(note.associatedid, { state: 'accepted' }));
            notificationsSeen.push(note.historyid);
            break;
          case 'archive_reject':
            // dispatch(clearArchivePending(note.associatedid));
            dispatch(setArchivePending(note.associatedid, { state: 'rejected', detail: note }));
            notificationsToLog.push(note);
            break;
          case 'archive_ready':
            // dispatch(clearArchivePending(note.associatedid));
            dispatch(setArchivePending(note.associatedid, { state: 'ready', detail: note }));
            notificationsToLog.push(note);
            break;
          case 'company_add':
            notificationsToLog.push(note);
            break;
          case 'company_apply':
            notificationsToLog.push(note);
            break;
          case 'bid_invite':
            // TODO: Inject item into view if view path matches
            notificationsSeen.push(note.historyid); // TODO: Hide new bid invites until BidSmall is ready, 6/18/19
            break;
          case 'bid_kick':
            dispatch(extractView([note.associatedid]));
            dispatch(extractSubview([note.associatedid]));
            notificationsSeen.push(note.historyid);
            break;
          case 'bid_update':
            // TODO: Seed item id into 'updated items' state
            notificationsSeen.push(note.historyid);
            // dispatch(getBid(note.associatedid));
            break;
          case 'project_update':
            notificationsSeen.push(note.historyid);
            // switch (note.associationtype) {
            //   case 'project':
            //     notificationsSeen.push(note.historyid);
            //     dispatch(getProject(note.associatedid));
            //     break;
            //   case 'submittal':
            //     notificationsSeen.push(note.historyid);
            //     dispatch(getSubmittal(note.parentid, note.associatedid));
            //     break;
            //   case 'dailylog':
            //     notificationsSeen.push(note.historyid);
            //     dispatch(getDailyLog(note.parentid, note.associatedid));
            //     break;
            //   case 'projectrfi':
            //     notificationsSeen.push(note.historyid);
            //     dispatch(getRfi(note.parentid, note.associatedid));
            //     break;
            //   case 'task':
            //     notificationsSeen.push(note.historyid);
            //     dispatch(getTask(note.parentid, note.associatedid));
            //     break;
            //   case 'punchlistitem':
            //     notificationsSeen.push(note.historyid);
            //     dispatch(getPunchList(note.parentid, note.associatedid));
            //     break;
            //   default:
            // }
            break;
          case 'project_invite':
            notificationsSeen.push(note.historyid);
            break;
          case 'project_kick':
            notificationsSeen.push(note.historyid);
            break;
          case 'profile_update':
            dispatch({ type: GET_MY_PROFILE_DONE, payload: note.fields });
            notificationsSeen.push(note.historyid);
            break;
          default:
            break;
        }
      } catch (error) {
        console.error(JSON.stringify(note));
        dispatch(networkError({...error, number: '10059-'+error.status}));
      }
    }
  } finally {
    if (notificationsSeen.length > 0) {
      try {
        let { response, status } = await markNotificationsSeen$({ notificationIds: notificationsSeen }).toPromise();
      } catch (error) {
        // If a notification can't be found, that's fine: ignore 404s on deleting notifications. Alert for other errors.
        if (error.status !== 404) {
          dispatch(networkError({ ...error, number: '10108-' + error.status }))
        }
      }
    }
    if (notificationsToLog.length > 0) {
      await persistNotifications(notificationsToLog, currentUser);
    }
  }

};

export const getNotifications = (initial = false) => async (dispatch, getState) => {
  try {
    let soFar = 0;
    let theSize = 0;
    let nextOffset, nextSortOffset;
    do {
      let { response, status } = await getNotifications$({ offset: nextOffset, sortoffset: nextSortOffset }).toPromise();
      if (status === 204) break;
      if (response === null) break;
      let { results, offset, size } = response;
      theSize = size;
      results = results || [];
      soFar += results.length;
      if (results.length > 0) {
        await handleNotifications(dispatch, getState, results);
      }
      if (soFar > MAX_SIZE) break;
      if (soFar < theSize) {
        nextOffset = R.last(results).historyid;
        nextSortOffset = R.last(results).datecreated;
      }
    } while (soFar < theSize);
    // encourage the server to switch to active notifications after our initial pull of data.
    if (initial) await switchToActiveNotifications$().toPromise();
  } catch (error) {
    dispatch(networkError({...error, number: '10057-'+error.status}));
  }
};

export const rehydrateNotifications = () => async (dispatch, getState) => {
  let currentUser = currentAuthenticatedUserId(getState());
  let notifications = [];
  await db.notifications.each(notification => {
    if (notification.userid === currentUser) {
      notifications.push(hydrate(notification));
    }
  });
  dispatch(addNotifications(notifications));
};

export const ackNotifications = (notificationIds) => async (dispatch, getState) => {
  try {
    let { response, status } = await markNotificationsSeen$({ notificationIds: R.clone(notificationIds) }).toPromise();
  } catch (error) {
    if (error.status !== 404) {
      dispatch(networkError({...error, number: '10058-'+error.status}));
    }
  }
  await depersistNotifications(notificationIds);
};



// WEBPACK FOOTER //
// ./src/actions/notifications.js